import {
  describeError,
  dirhams,
  FINANCE_ERRORS,
  type ResultOf,
  useSession,
} from '@horof/shared';
import { useQuery } from 'urql';
import { graphql } from './gql';

/** The choices on every form and filter, cached for the whole session. */
export const ListsDocument = graphql(`
  query DeskLists {
    projects(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        description
        status
        closedOn
        closedReason
        reportLink
      }
    }
    financialAccounts(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        type
        accountNumber
        openingBalance
        openingDate
        currentBalance
      }
    }
    operationCategories(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        appliesTo
      }
    }
    counterparties(orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        kind
        phone
        note
      }
    }
    currentSettings {
      receiptPhotoMinAmount
    }
  }
`);

export type Lists = ResultOf<typeof ListsDocument>;
type Node<T extends { nodes: unknown[] } | null> = NonNullable<
  NonNullable<T>['nodes'][number]
>;
export type Project = Node<Lists['projects']>;
export type Account = Node<Lists['financialAccounts']>;
export type Category = Node<Lists['operationCategories']>;
export type Counterparty = Node<Lists['counterparties']>;

/**
 * Refetch after any finance change. urql refreshes a cached query only for
 * the types it saw in it, and an empty list shows none.
 */
export const financeContext = {
  additionalTypenames: [
    'FinancialOperation',
    'AccountTransfer',
    'FinancialAccount',
    'Project',
    'Counterparty',
    'OperationCategory',
    'Debt',
    'InKindContribution',
  ],
};

export function useLists() {
  return useQuery({ query: ListsDocument, context: financeContext });
}

/** The count in the side menu. */
export const ReviewCountDocument = graphql(`
  query DeskReviewCount {
    financeReviewQueue {
      totalCount
    }
  }
`);

export const OperationsTableDocument = graphql(`
  query DeskOperations(
    $filter: FinancialOperationFilter
    $orderBy: [FinancialOperationOrderBy!]
    $first: Int!
    $offset: Int!
  ) {
    financialOperations(
      filter: $filter
      orderBy: $orderBy
      first: $first
      offset: $offset
    ) {
      totalCount
      nodes {
        id
        date
        type
        amount
        description
        needsReview
        reviewNote
        source
        transferId
        debtId
        projectId
        accountId
        categoryId
        counterpartyId
        receiptPhoto {
          id
          url
          contentType
        }
        project {
          id
          name
          status
        }
        account {
          id
          name
        }
        category {
          id
          name
        }
        counterparty {
          id
          name
        }
      }
    }
  }
`);

export type TableOperation = NonNullable<
  NonNullable<
    ResultOf<typeof OperationsTableDocument>['financialOperations']
  >['nodes'][number]
>;

export const OperationDocument = graphql(`
  query DeskOperation($id: UUID!) {
    financialOperation(id: $id) {
      id
      date
      type
      amount
      description
      projectId
      accountId
      categoryId
      counterpartyId
      debtId
      transferId
      source
      needsReview
      reviewNote
      receiptPhotoId
      createdAt
      updatedAt
      receiptPhoto {
        id
        url
        contentType
        createdAt
        uploadedBy
      }
      project {
        id
        name
        status
        closedOn
      }
      debt {
        id
        direction
        purpose
        counterparty {
          name
        }
      }
    }
  }
`);

export type DetailOperation = NonNullable<
  ResultOf<typeof OperationDocument>['financialOperation']
>;

export const CreateOperationDocument = graphql(`
  mutation DeskCreateOperation($operation: FinancialOperationInput!) {
    createFinancialOperation(input: { financialOperation: $operation }) {
      financialOperation {
        id
      }
    }
  }
`);

export const UpdateOperationDocument = graphql(`
  mutation DeskUpdateOperation(
    $id: UUID!
    $patch: FinancialOperationPatch!
  ) {
    updateFinancialOperation(input: { id: $id, patch: $patch }) {
      financialOperation {
        id
        date
        type
        amount
        description
        projectId
        accountId
        categoryId
        counterpartyId
        needsReview
        reviewNote
        receiptPhotoId
        category {
          id
          name
        }
        counterparty {
          id
          name
        }
        project {
          id
          name
          status
        }
      }
    }
  }
`);

export const DeleteOperationDocument = graphql(`
  mutation DeskDeleteOperation($id: UUID!) {
    deleteFinancialOperation(input: { id: $id }) {
      financialOperation {
        id
      }
    }
  }
`);

export const CreateCounterpartyDocument = graphql(`
  mutation DeskCreateCounterparty($counterparty: CounterpartyInput!) {
    createCounterparty(input: { counterparty: $counterparty }) {
      counterparty {
        id
        name
        kind
      }
    }
  }
`);

export const UpdateCounterpartyDocument = graphql(`
  mutation DeskUpdateCounterparty($id: UUID!, $patch: CounterpartyPatch!) {
    updateCounterparty(input: { id: $id, patch: $patch }) {
      counterparty {
        id
        name
        kind
        phone
        note
      }
    }
  }
`);

export const DeleteCounterpartyDocument = graphql(`
  mutation DeskDeleteCounterparty($id: UUID!) {
    deleteCounterparty(input: { id: $id }) {
      counterparty {
        id
      }
    }
  }
`);

export const CreateCategoryDocument = graphql(`
  mutation DeskCreateCategory($category: OperationCategoryInput!) {
    createOperationCategory(input: { operationCategory: $category }) {
      operationCategory {
        id
        name
        appliesTo
      }
    }
  }
`);

export const UpdateCategoryDocument = graphql(`
  mutation DeskUpdateCategory($id: UUID!, $patch: OperationCategoryPatch!) {
    updateOperationCategory(input: { id: $id, patch: $patch }) {
      operationCategory {
        id
        name
        appliesTo
      }
    }
  }
`);

export const DeleteCategoryDocument = graphql(`
  mutation DeskDeleteCategory($id: UUID!) {
    deleteOperationCategory(input: { id: $id }) {
      operationCategory {
        id
      }
    }
  }
`);

export const CreateProjectDocument = graphql(`
  mutation DeskCreateProject($project: ProjectInput!) {
    createProject(input: { project: $project }) {
      project {
        id
        name
        status
      }
    }
  }
`);

export const UpdateProjectDocument = graphql(`
  mutation DeskUpdateProject($id: UUID!, $patch: ProjectPatch!) {
    updateProject(input: { id: $id, patch: $patch }) {
      project {
        id
        name
        description
        status
        closedOn
        closedReason
      }
    }
  }
`);

export const CreateAccountDocument = graphql(`
  mutation DeskCreateAccount($account: FinancialAccountInput!) {
    createFinancialAccount(input: { financialAccount: $account }) {
      financialAccount {
        id
      }
    }
  }
`);

export const UpdateAccountDocument = graphql(`
  mutation DeskUpdateAccount($id: UUID!, $patch: FinancialAccountPatch!) {
    updateFinancialAccount(input: { id: $id, patch: $patch }) {
      financialAccount {
        id
        name
        type
        accountNumber
        openingBalance
        openingDate
        currentBalance
      }
    }
  }
`);

/** The treasurer roles write the finances; the committee reads them. */
export function useCanWrite(): boolean {
  return useSession().roles.includes('treasurer');
}

/**
 * Why a row can't be edited, or null: the committee reads only, a
 * transfer's operation changes with its transfer, a closed project freezes
 * its operations.
 */
export function lockReason(
  op: {
    transferId: string | null;
    project: { status: 'OPEN' | 'CLOSED' } | null;
  },
  canWrite: boolean,
): string | null {
  if (!canWrite) return 'للاطلاع فقط: أمين المال وحده يعدّل العمليات.';
  if (op.transferId) return 'هذه العملية جزء من تحويل: عدّل التحويل نفسه';
  if (op.project?.status === 'CLOSED') return 'المشروع مغلق: عملياته مجمّدة.';
  return null;
}

/** A signed link to the operations as .xlsx or .csv (arqam doc 58). */
export const FinanceExportLinkDocument = graphql(`
  query DeskFinanceExport(
    $filter: FinanceExportFilter
    $format: FinanceExportFormat
  ) {
    financeExportLink(filter: $filter, format: $format)
  }
`);

/** One change for many operations, all or nothing (arqam doc 56). */
export const UpdateOperationsDocument = graphql(`
  mutation DeskUpdateOperations($ids: [UUID]!, $patch: JSON!) {
    appUpdateFinancialOperations(input: { ids: $ids, patch: $patch }) {
      result
    }
  }
`);

/**
 * A bulk change's error. arqam names the refused row (`operationId`, `date`,
 * `amount`); `label` is «العملية بتاريخ … مبلغ …» for showing it apart.
 */
export function bulkError(error: Parameters<typeof describeError>[0]): {
  message: string;
  operationId: string | null;
  label: string | null;
} {
  const values = (error?.graphQLErrors[0]?.extensions ?? {}) as Record<
    string,
    unknown
  >;
  const message = describeError(error, 'تعذّر الحفظ.', FINANCE_ERRORS);
  const operationId =
    typeof values.operationId === 'string' ? values.operationId : null;
  return {
    message,
    operationId,
    label: operationId
      ? `العملية بتاريخ ${values.date} مبلغ ${dirhams(String(values.amount))}`
      : null,
  };
}
